import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { useImportBatch } from '../ImportBatchContext'
import { BD, Button, Card, KpiCard, KpiGrid, PageSubtitle, PageTitle, Spinner, Table } from '../styles'
import { fmtMoney } from '../statusUi'

const STATUS_UI: Record<string, { label: string; bg: string; color: string }> = {
  pending: { label: 'Aguardando', bg: '#F3F4F6', color: '#4B5563' },
  running: { label: 'Processando…', bg: '#FEF3C7', color: '#92400E' },
  success: { label: 'Importada', bg: '#D1FAE5', color: '#065F46' },
  error: { label: 'Erro', bg: '#FEE2E2', color: '#991B1B' },
}

export const ImportacaoOs: FC = () => {
  const navigate = useNavigate()
  const { batch, reprocessarOS, reprocessarErros } = useImportBatch()

  const total = batch.length
  const ok = batch.filter((b) => b.status === 'success').length
  const erro = batch.filter((b) => b.status === 'error').length
  const pend = batch.filter((b) => b.status === 'pending' || b.status === 'running').length
  const pct = total ? Math.round(((ok + erro) / total) * 100) : 0
  const emAndamento = pend > 0

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>Importação de OS para o Fechamento</PageTitle>
          <PageSubtitle>Lançamento dos custos operacionais aprovados no fechamento (P2) do evento.</PageSubtitle>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button $variant="secondary" disabled={erro === 0 || emAndamento} onClick={reprocessarErros}>
            ↻ Reprocessar erros
          </Button>
          <Button disabled={emAndamento} onClick={() => navigate('/interno/os')}>← Voltar para listagem</Button>
        </div>
      </div>

      {total === 0 ? (
        <Card style={{ textAlign: 'center', padding: 48, color: BD.grayText }}>
          Nenhum lote ativo. Selecione OS aprovadas na{' '}
          <span style={{ color: BD.red, cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate('/interno/os')}>
            listagem
          </span>{' '}
          e clique em “Importar OS”.
        </Card>
      ) : (
        <>
          <div
            style={{
              padding: 12,
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 13,
              fontWeight: 600,
              background: emAndamento ? '#EFF6FF' : erro > 0 ? '#FEF3C7' : '#D1FAE5',
              color: emAndamento ? '#1E3A8A' : erro > 0 ? '#92400E' : '#065F46',
            }}
          >
            {emAndamento
              ? '⏳ Importação em andamento — não feche esta tela.'
              : erro > 0
              ? `⚠️ Importação concluída com ${erro} erro(s). Você pode reprocessar abaixo.`
              : `✓ Importação concluída com sucesso. ${ok} OS lançadas no fechamento.`}
          </div>

          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontWeight: 700 }}>Progresso</span>
              <span style={{ fontWeight: 800, color: BD.red }}>{pct}%</span>
            </div>
            <div style={{ width: '100%', height: 10, background: '#E5E7EB', borderRadius: 9999 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: BD.red, borderRadius: 9999, transition: 'width .3s' }} />
            </div>
          </Card>

          <KpiGrid style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <KpiCard><div className="kpi-label">Total</div><div className="kpi-value">{total}</div></KpiCard>
            <KpiCard><div className="kpi-label">Pendentes</div><div className="kpi-value" style={{ color: '#92400E' }}>{pend}</div></KpiCard>
            <KpiCard><div className="kpi-label">Sucesso</div><div className="kpi-value" style={{ color: '#065F46' }}>{ok}</div></KpiCard>
            <KpiCard><div className="kpi-label">Erro</div><div className="kpi-value" style={{ color: '#991B1B' }}>{erro}</div></KpiCard>
          </KpiGrid>

          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <Table>
              <thead>
                <tr>
                  <th>Nº OS</th><th>Evento</th><th>Produtor</th>
                  <th style={{ textAlign: 'right' }}>Custo</th><th>Status</th><th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {batch.map((b) => {
                  const ui = STATUS_UI[b.status]
                  return (
                    <tr key={b.osId} style={{ cursor: 'default' }}>
                      <td><span style={{ fontFamily: 'monospace', fontWeight: 600 }}>OS {b.numero}</span></td>
                      <td style={{ fontWeight: 500 }}>{b.evento}</td>
                      <td style={{ color: BD.grayText }}>{b.produtor}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(b.total)}</td>
                      <td>
                        {b.status === 'running' ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: '#92400E' }}>
                            <Spinner aria-hidden="true" style={{ width: 14, height: 14, margin: 0, borderWidth: 2 }} /> Processando…
                          </span>
                        ) : (
                          <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 600, background: ui.bg, color: ui.color }}>
                            {ui.label}
                          </span>
                        )}
                        {b.status === 'error' && <div style={{ fontSize: 12, color: '#991B1B', marginTop: 4 }}>{b.erro}</div>}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {b.status === 'error' && (
                          <Button $variant="secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => reprocessarOS(b.osId)}>
                            ↻ Reprocessar
                          </Button>
                        )}
                        {b.status === 'success' && <span style={{ color: '#065F46', fontSize: 13 }}>✓</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </Card>
        </>
      )}
    </>
  )
}

export default ImportacaoOs
